import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as d3 from 'd3';

// Two-ring donut: inner ring = parent group (MS / SomaScan / Both),
// outer ring = subcategory (with SomaScan split into unspecified + 7k).
// Outer-slice angles are computed inside their parent's angle range, so
// shares stay visually accurate at every level.

export const PLATFORM_GROUPS = [
  {
    id: 'MS',
    label: 'MS',
    subs: [{ id: 'MS', label: 'MS' }],
  },
  {
    id: 'SomaScan',
    label: 'SomaScan',
    subs: [
      { id: 'SomaScan_unspec', label: 'SomaScan (unspecified)' },
      { id: 'SomaScan_7k', label: 'SomaScan 7k' },
    ],
  },
  {
    id: 'Both',
    label: 'MS + SomaScan',
    subs: [
      { id: 'Both_unspec', label: 'MS + SomaScan (unspecified)' },
      { id: 'Both_7k', label: 'MS + SomaScan 7k' },
    ],
  },
];

export const PLATFORM_GROUP_OF_SUB = PLATFORM_GROUPS.reduce((acc, g) => {
  for (const sub of g.subs) acc[sub.id] = g.id;
  return acc;
}, {});

const COLORS = {
  light: {
    group: { MS: '#2a6fc7', SomaScan: '#2c8c4a', Both: '#7339b8' },
    sub: {
      MS: '#2a6fc7',
      SomaScan_unspec: '#2c8c4a',
      SomaScan_7k: '#5cbd75',
      Both_unspec: '#7339b8',
      Both_7k: '#a079d6',
    },
    selected: '#fb923c',
  },
  dark: {
    group: { MS: '#5fa8ff', SomaScan: '#5fc977', Both: '#a980eb' },
    sub: {
      MS: '#5fa8ff',
      SomaScan_unspec: '#5fc977',
      SomaScan_7k: '#9bdfa9',
      Both_unspec: '#a980eb',
      Both_7k: '#cdb1f3',
    },
    selected: '#ffe17a',
  },
};

export default function PlatformDonutChart({
  rowData,
  selectedPlatforms,
  onToggleSub,
  onToggleGroup,
  isDark,
}) {
  const containerRef = useRef(null);
  const svgRef = useRef(null);
  const [width, setWidth] = useState(0);

  const counts = useMemo(() => {
    const subCounts = {};
    for (const g of PLATFORM_GROUPS) {
      for (const s of g.subs) subCounts[s.id] = 0;
    }
    if (Array.isArray(rowData)) {
      for (const row of rowData) {
        const sub = row.platformSubcategory;
        if (sub && Object.prototype.hasOwnProperty.call(subCounts, sub)) {
          subCounts[sub] += 1;
        }
      }
    }
    const groupCounts = {};
    for (const g of PLATFORM_GROUPS) {
      groupCounts[g.id] = g.subs.reduce((s, sub) => s + subCounts[sub.id], 0);
    }
    return { sub: subCounts, group: groupCounts };
  }, [rowData]);

  const total = useMemo(
    () => Object.values(counts.group).reduce((s, n) => s + n, 0),
    [counts]
  );

  useEffect(() => {
    if (!containerRef.current) return;
    const el = containerRef.current;
    const update = () => setWidth(el.clientWidth || 0);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    if (!svgRef.current || width <= 0) return;

    const W = width;
    const H = 440;
    const cx = W / 2;
    const cy = H / 2 - 4;
    const labelMargin = 140;
    const maxOuter = Math.max(60, Math.min((W - labelMargin * 2) * 0.5, H * 0.42));
    const innerHole = Math.max(28, maxOuter * 0.28);
    const ringSplit = innerHole + (maxOuter - innerHole) * 0.55;

    const fg = isDark ? '#ddd' : '#222';
    const palette = isDark ? COLORS.dark : COLORS.light;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();
    svg.attr('viewBox', `0 0 ${W} ${H}`)
       .attr('width', '100%')
       .attr('height', H);

    if (total === 0) {
      svg.append('text')
        .attr('x', cx).attr('y', cy)
        .attr('text-anchor', 'middle')
        .attr('fill', fg).attr('font-size', 14)
        .text('No platform data');
      return;
    }

    // Build a single list of slice descriptors. Single-sub groups (e.g. MS)
    // collapse into one unified slice that spans the full ring height.
    const TWO_PI = Math.PI * 2;
    let cursor = -Math.PI / 2; // start at 12 o'clock
    const slices = [];
    for (const group of PLATFORM_GROUPS) {
      const groupCount = counts.group[group.id];
      const groupSpan = (groupCount / total) * TWO_PI;
      const groupStart = cursor;
      const groupEnd = cursor + groupSpan;

      if (group.subs.length === 1) {
        const sub = group.subs[0];
        const isSelected = selectedPlatforms.has(sub.id);
        slices.push({
          kind: 'unified',
          label: group.label,
          count: groupCount,
          startAngle: groupStart,
          endAngle: groupEnd,
          innerR: innerHole,
          outerR: maxOuter,
          baseColor: palette.group[group.id],
          isSelected,
          onClick: () => onToggleSub(sub.id, !isSelected),
        });
      } else {
        const subIds = group.subs.map(s => s.id);
        const nonEmptySubIds = subIds.filter(id => counts.sub[id] > 0);
        const groupSelected = nonEmptySubIds.length > 0
          && nonEmptySubIds.every(id => selectedPlatforms.has(id));
        slices.push({
          kind: 'parent',
          label: group.label,
          count: groupCount,
          startAngle: groupStart,
          endAngle: groupEnd,
          innerR: innerHole,
          outerR: ringSplit,
          baseColor: palette.group[group.id],
          isSelected: groupSelected,
          onClick: () => onToggleGroup(group.id),
        });

        let subCursor = groupStart;
        for (const sub of group.subs) {
          const subCount = counts.sub[sub.id];
          const subSpan = groupCount > 0 ? (subCount / groupCount) * groupSpan : 0;
          const isSelected = selectedPlatforms.has(sub.id);
          slices.push({
            kind: 'sub',
            label: sub.label,
            count: subCount,
            startAngle: subCursor,
            endAngle: subCursor + subSpan,
            innerR: ringSplit + 1,
            outerR: maxOuter,
            baseColor: palette.sub[sub.id],
            isSelected,
            onClick: () => onToggleSub(sub.id, !isSelected),
          });
          subCursor += subSpan;
        }
      }
      cursor = groupEnd;
    }

    const visible = slices.filter(s =>
      (s.endAngle - s.startAngle) > 0 && s.outerR > s.innerR
    );

    const arcGen = d3.arc().padAngle(0.005).cornerRadius(2);
    const g = svg.append('g').attr('transform', `translate(${cx},${cy})`);

    // Slices
    const sliceSel = g.selectAll('path.platform-slice')
      .data(visible)
      .join('path')
      .attr('class', 'platform-slice')
      .attr('d', d => arcGen
        .innerRadius(d.innerR)
        .outerRadius(d.outerR)({ startAngle: d.startAngle, endAngle: d.endAngle }))
      .attr('fill', d => d.isSelected ? palette.selected : d.baseColor)
      .attr('stroke', d => d.isSelected ? fg : (isDark ? '#222' : '#fff'))
      .attr('stroke-width', d => d.isSelected ? 2 : 1)
      .style('cursor', d => d.count > 0 ? 'pointer' : 'default')
      .on('mouseover', function (_e, d) {
        if (d.count > 0 && !d.isSelected) {
          d3.select(this).attr('fill', d3.color(d.baseColor).brighter(0.3).formatHex());
        }
      })
      .on('mouseout', function (_e, d) {
        if (!d.isSelected) d3.select(this).attr('fill', d.baseColor);
      })
      .on('click', (_e, d) => { if (d.count > 0) d.onClick(); });

    sliceSel.append('title').text(d => {
      const pct = (d.count / total * 100).toFixed(1);
      const action = d.isSelected ? 'remove' : 'apply';
      return `${d.label}\n${d.count} proteins (${pct}%)\nClick to ${action} filter`;
    });

    // Inline count labels (numbers on top of slices)
    g.selectAll('text.platform-count-label')
      .data(visible)
      .join('text')
      .attr('class', 'platform-count-label')
      .attr('text-anchor', 'middle')
      .attr('fill', '#fff')
      .style('pointer-events', 'none')
      .style('paint-order', 'stroke')
      .style('stroke', 'rgba(0,0,0,0.45)')
      .style('stroke-width', '2px')
      .each(function (d) {
        const node = d3.select(this);
        node.text('');
        const angle = d.endAngle - d.startAngle;
        const minAngle = d.kind === 'sub' ? 0.18 : 0.22;
        if (d.count <= 0 || angle < minAngle) return;
        const midR = (d.innerR + d.outerR) / 2;
        const midA = (d.startAngle + d.endAngle) / 2;
        const lx = midR * Math.sin(midA);
        const ly = -midR * Math.cos(midA);
        node.attr('transform', `translate(${lx},${ly})`);
        const showPercent = d.kind !== 'sub' && (d.outerR - d.innerR) > 35;
        if (showPercent) {
          const pct = (d.count / total * 100).toFixed(0);
          node.append('tspan')
            .attr('x', 0).attr('dy', '-0.1em')
            .attr('font-size', 12).attr('font-weight', 700)
            .text(`${d.count}`);
          node.append('tspan')
            .attr('x', 0).attr('dy', '1.15em')
            .attr('font-size', 10).attr('font-weight', 500)
            .text(`${pct}%`);
        } else {
          node.append('tspan')
            .attr('x', 0).attr('dy', '0.32em')
            .attr('font-size', 11).attr('font-weight', 700)
            .text(`${d.count}`);
        }
      });

    // Outer leader-line labels with the platform name. Only for slices that
    // touch the donut edge (unified slices and outer-ring sub slices).
    const labelSlices = visible.filter(s =>
      s.count > 0 && s.outerR === maxOuter && s.kind !== 'parent'
    );
    for (const s of labelSlices) {
      const midA = (s.startAngle + s.endAngle) / 2;
      const sinA = Math.sin(midA);
      const cosA = Math.cos(midA);
      const r1 = maxOuter + 4;
      const r2 = maxOuter + 22;
      const x1 = r1 * sinA;
      const y1 = -r1 * cosA;
      const x2 = r2 * sinA;
      const y2 = -r2 * cosA;
      const isRight = sinA >= 0;
      const x3 = x2 + (isRight ? 8 : -8);

      g.append('polyline')
        .attr('points', `${x1},${y1} ${x2},${y2} ${x3},${y2}`)
        .attr('stroke', fg)
        .attr('stroke-opacity', 0.5)
        .attr('fill', 'none')
        .style('pointer-events', 'none');

      g.append('text')
        .attr('x', x3 + (isRight ? 4 : -4))
        .attr('y', y2)
        .attr('dy', '0.32em')
        .attr('text-anchor', isRight ? 'start' : 'end')
        .attr('fill', fg)
        .attr('font-size', 12)
        .attr('font-weight', s.isSelected ? 700 : 500)
        .style('pointer-events', 'none')
        .text(s.label);
    }

    // Center total
    const center = g.append('g').style('pointer-events', 'none');
    center.append('text')
      .attr('text-anchor', 'middle').attr('y', -2)
      .attr('fill', fg).attr('font-size', 18).attr('font-weight', 700)
      .text(`${total}`);
    center.append('text')
      .attr('text-anchor', 'middle').attr('y', 14)
      .attr('fill', fg).attr('font-size', 10).attr('opacity', 0.75)
      .text('proteins');
  }, [counts, total, width, isDark, selectedPlatforms, onToggleSub, onToggleGroup]);

  const palette = isDark ? COLORS.dark : COLORS.light;

  return (
    <div ref={containerRef} style={{ width: '100%' }}>
      <svg ref={svgRef} style={{ display: 'block' }} />
      <div style={{ textAlign: 'center', fontSize: '0.8rem', opacity: 0.75, marginTop: 4 }}>
        Click an inner-ring slice to toggle a whole group; click an outer slice for a specific subcategory.
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '10px 14px',
          flexWrap: 'wrap',
          marginTop: 10,
        }}
        role="group"
        aria-label="Platform legend"
      >
        {PLATFORM_GROUPS.map(group => {
          const groupCount = counts.group[group.id];
          const subIds = group.subs.map(s => s.id);
          const nonEmpty = subIds.filter(id => counts.sub[id] > 0);
          const allSelected = nonEmpty.length > 0 && nonEmpty.every(id => selectedPlatforms.has(id));
          const groupColor = allSelected ? palette.selected : palette.group[group.id];
          return (
            <div
              key={group.id}
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
                padding: '6px 10px',
                border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)'}`,
                borderRadius: 6,
                minWidth: 170,
              }}
            >
              <button
                type="button"
                onClick={() => groupCount > 0 && onToggleGroup(group.id)}
                disabled={groupCount === 0}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  background: 'transparent',
                  border: 'none',
                  padding: 0,
                  color: 'inherit',
                  cursor: groupCount > 0 ? 'pointer' : 'default',
                  fontSize: '0.92rem',
                  fontWeight: allSelected ? 700 : 600,
                  textAlign: 'left',
                  opacity: groupCount === 0 ? 0.5 : 1,
                }}
                title={groupCount > 0
                  ? `Click to ${allSelected ? 'remove' : 'apply'} the ${group.label} group filter`
                  : 'No proteins in this group'}
              >
                <span style={{
                  display: 'inline-block',
                  width: 14,
                  height: 14,
                  borderRadius: 3,
                  background: groupColor,
                  border: allSelected ? `1.5px solid ${isDark ? '#ddd' : '#222'}` : 'none',
                }} />
                <span>{group.label}: <strong>{groupCount}</strong></span>
              </button>
              {group.subs.length > 1 && group.subs.map(sub => {
                const subCount = counts.sub[sub.id];
                const isSelected = selectedPlatforms.has(sub.id);
                const subColor = isSelected ? palette.selected : palette.sub[sub.id];
                return (
                  <button
                    key={sub.id}
                    type="button"
                    onClick={() => subCount > 0 && onToggleSub(sub.id, !isSelected)}
                    disabled={subCount === 0}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 8,
                      background: 'transparent',
                      border: 'none',
                      padding: '0 0 0 14px',
                      color: 'inherit',
                      cursor: subCount > 0 ? 'pointer' : 'default',
                      fontSize: '0.85rem',
                      fontWeight: isSelected ? 700 : 400,
                      textAlign: 'left',
                      opacity: subCount === 0 ? 0.5 : 1,
                    }}
                    title={subCount > 0
                      ? `Click to ${isSelected ? 'remove' : 'apply'} ${sub.label}`
                      : 'No proteins in this subcategory'}
                  >
                    <span style={{
                      display: 'inline-block',
                      width: 11,
                      height: 11,
                      borderRadius: 2,
                      background: subColor,
                      border: isSelected ? `1.5px solid ${isDark ? '#ddd' : '#222'}` : 'none',
                    }} />
                    <span>{sub.label}: <strong>{subCount}</strong></span>
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}
